import firebase from 'firebase/app'
import 'firebase/firestore'
import format from 'date-fns/format'
import subDays from 'date-fns/sub_days'

export const AXIS_OPTIONS = [
  { key: 'impact_rating', label: 'Impact Rating', min: 0, max: 150, dynamic: true, highIsGood: true,
    description: 'Overall contribution score based on engagement behavior and organizational impact.',
    dataNote: 'Composite score derived from check-in activity, feedback participation, recognition patterns, and team involvement. Updated weekly. Scores above 70 indicate strong engagement.' },
  { key: 'computed_flight_risk', label: 'Flight Risk Score', min: 0, max: 100, dynamic: false, highIsGood: false,
    description: 'Likelihood of disengagement based on unhappy check-in patterns.',
    dataNote: 'Percentage of unhappy daily check-in responses over the past 90 days, scaled non-linearly so even occasional unhappy patterns produce a meaningful signal.' },
  { key: 'computed_engagement', label: 'Engagement Score', min: 0, max: 100, dynamic: false, highIsGood: true,
    description: 'How actively someone participates across check-ins, feedback, and recognition.',
    dataNote: 'Combines 6 monthly indicators: daily check-ins (30%), open-ended feedback (20%), recognition activity (20%), manager feedback (10%), recognition quality (10%), and peer feedback (10%). Based on the most recent month.' },
  { key: 'computed_trust', label: 'Trust Score', min: 0, max: 100, dynamic: false, highIsGood: true,
    description: 'How trusted and valued someone is by their peers.',
    dataNote: 'Combines peer feedback activity (30%), recognition reciprocity (30%), network relationship depth (20%), and recognition quality received (20%). Based on last 6 months.' },
  { key: 'computed_focus', label: 'Focus Score', min: 0, max: 100, dynamic: false, highIsGood: true,
    description: 'How actively someone sets focus items for their development.',
    dataNote: 'Based on the number of active focus items. Each item contributes 20 points, up to 5 items for a maximum score of 100.' },
  { key: 'computed_goal_alignment', label: 'Goal Alignment', min: 0, max: 100, dynamic: false, highIsGood: true,
    description: 'How well someone\'s focus items connect to team goals.',
    dataNote: 'Percentage of focus items linked to a team goal. Shows 0 if no focus items are set.' },
  { key: 'computed_performance', label: 'Performance Score', min: 0, max: 100, dynamic: false, highIsGood: true,
    description: 'How consistently someone earns gems in performance boosts.',
    dataNote: 'Gem rate (culture and goals both rated 3+) weighted by number of interactions. More boosts with gems = higher score. Based on last 6 months.' },
  { key: 'computed_giving_recognition', label: 'Giving Recognition', min: 0, max: 100, dynamic: false, highIsGood: true,
    description: 'How actively someone recognizes others\' contributions.',
    dataNote: 'Number of recognitions given over the past 6 months, normalized against the team median. A score of 50 means typical activity; 100 means twice the median or more.' },
  { key: 'computed_receiving_recognition', label: 'Receiving Recognition', min: 0, max: 100, dynamic: false, highIsGood: true,
    description: 'How much recognition someone receives from peers.',
    dataNote: 'Number of recognitions received over the past 6 months, normalized against the team median. A score of 50 means typical recognition; 100 means twice the median or more.' },
  { key: 'healthbar', label: 'Happiness Score', min: 0, max: 100, dynamic: false, highIsGood: true,
    description: 'Current happiness and well-being level.',
    dataNote: 'Based on daily check-in responses and survey data. Reflects recent mood trends.' },
  { key: 'computed_peer_trust', label: 'Peer Trust Count', min: 0, max: 15, dynamic: true, highIsGood: true,
    description: 'Breadth of peer relationships through feedback and mutual recognition.',
    dataNote: 'Count of unique peers given feedback to, unique peers received feedback from, and reciprocal recognition pairs (mutual give-and-receive). Based on last 6 months.' },
  { key: 'medalCount', label: 'Recognition Medals', min: 0, max: 30, dynamic: true, highIsGood: true,
    description: 'Total special recognition medals earned.',
    dataNote: 'Cumulative count of medals received across all recognition events. Not time-bounded.' },
  { key: 'ps_empathy', label: 'Empathy (EQ)', min: 0, max: 10, dynamic: false, highIsGood: true,
    description: 'Ability to understand and share the feelings of others.',
    dataNote: 'Score from 0-10 based on power skills assessment responses over time.' },
  { key: 'ps_self_awareness', label: 'Self-Awareness (EQ)', min: 0, max: 10, dynamic: false, highIsGood: true,
    description: 'Understanding of own emotions, strengths, and limitations.',
    dataNote: 'Score from 0-10 based on power skills assessment responses over time.' },
  { key: 'ps_critical_thinking', label: 'Critical Thinking (EQ)', min: 0, max: 10, dynamic: false, highIsGood: true,
    description: 'Ability to analyze situations and make reasoned decisions.',
    dataNote: 'Score from 0-10 based on power skills assessment responses over time.' },
  { key: 'ps_leadership', label: 'Leadership (EQ)', min: 0, max: 10, dynamic: false, highIsGood: true,
    description: 'Ability to guide, inspire, and influence others.',
    dataNote: 'Score from 0-10 based on power skills assessment responses over time.' },
  { key: 'ps_initiative_making', label: 'Initiative (EQ)', min: 0, max: 10, dynamic: false, highIsGood: true,
    description: 'Proactiveness in taking action and driving outcomes.',
    dataNote: 'Score from 0-10 based on power skills assessment responses over time.' },
  { key: 'ps_optimism', label: 'Optimism (EQ)', min: 0, max: 10, dynamic: false, highIsGood: true,
    description: 'Positive outlook and resilience in the face of challenges.',
    dataNote: 'Score from 0-10 based on power skills assessment responses over time.' },
]

export default {
  namespaced: true,
  state: {
    selectedXAxis: 'impact_rating',
    selectedYAxis: 'healthbar',
    selectedTeam: '',
    colorBy: 'talent_type',
    loading: false,
    selectedEmployee: null,
    compositeScores: {} as Record<string, any>,
    compositeLoaded: false,
  },
  mutations: {
    setSelectedXAxis(state: any, value: string) {
      state.selectedXAxis = value
    },
    setSelectedYAxis(state: any, value: string) {
      state.selectedYAxis = value
    },
    setSelectedTeam(state: any, value: string) {
      state.selectedTeam = value
    },
    setColorBy(state: any, value: string) {
      state.colorBy = value
    },
    setLoading(state: any, value: boolean) {
      state.loading = value
    },
    setSelectedEmployee(state: any, value: any) {
      state.selectedEmployee = value
    },
    setCompositeScores(state: any, value: any) {
      state.compositeScores = value
      state.compositeLoaded = true
    },
  },
  getters: {
    selectedXAxis: (state: any) => state.selectedXAxis,
    selectedYAxis: (state: any) => state.selectedYAxis,
    selectedTeam: (state: any) => state.selectedTeam,
    colorBy: (state: any) => state.colorBy,
    loading: (state: any) => state.loading,
    selectedEmployee: (state: any) => state.selectedEmployee,
    compositeScores: (state: any) => state.compositeScores,
  },
  actions: {
    async loadMatrixData({ dispatch, rootState, commit }: any, user: any) {
      commit('setLoading', true)
      try {
        if (!rootState.people.membersall || rootState.people.membersall.length === 0) {
          await dispatch('people/getStarAndRiskPerform', user, { root: true })
        }
      } finally {
        commit('setLoading', false)
      }
    },

    async loadCompositeScores({ rootState, commit, state }: any, user: any) {
      if (!user || !user.company) return

      const scores: Record<string, any> = {}
      const now = new Date()
      const past90 = format(subDays(now, 90), 'YYYY-MM-DD')
      const past180 = format(subDays(now, 180), 'YYYY-MM-DD')

      // --- Build month keys for report_monthly ---
      const monthKeys: string[] = []
      for (let i = 0; i < 3; i++) {
        monthKeys.push(format(subDays(now, i * 30), 'YYYY-MM'))
      }
      const uniqueMonths = [...new Set(monthKeys)]

      // --- Fire all Firestore queries in parallel ---
      const [monthlySnap, focusSnap, reviewSnap, hereSnap, feedbackSnap] = await Promise.all([
        firebase.firestore().collection('report_monthly')
          .where('company', '==', user.company)
          .where('monthyear', 'in', uniqueMonths)
          .get().catch(e => { console.warn('Failed to load report_monthly:', e); return null }),
        firebase.firestore().collection('focus_summary_latest')
          .where('company', '==', user.company)
          .get().catch(e => { console.warn('Failed to load focus data:', e); return null }),
        firebase.firestore().collection('send_review_gem')
          .where('company', '==', user.company)
          .where('createdate', '>=', past180)
          .get().catch(e => { console.warn('Failed to load performance data:', e); return null }),
        firebase.firestore().collection('here_point')
          .where('company', '==', user.company)
          .where('date', '>=', past180)
          .where('statusrow', 'in', ['sendcoin'])
          .get().catch(e => { console.warn('Failed to load recognition data:', e); return null }),
        firebase.firestore().collection('peerfeedback')
          .where('company', '==', user.company)
          .where('date', '>=', past180)
          .get().catch(e => { console.warn('Failed to load peerfeedback data:', e); return null }),
      ])

      // --- Process report_monthly for flight risk + engagement ---
      const flightRiskByEmail: Record<string, { red: number, total: number }> = {}
      const engagementByEmail: Record<string, any> = {}
      if (monthlySnap) {
        monthlySnap.forEach((doc: any) => {
          const data = doc.data()
          if (!data.email) return

          if (data.daily_answer) {
            if (!flightRiskByEmail[data.email]) {
              flightRiskByEmail[data.email] = { red: 0, total: 0 }
            }
            const answers = data.daily_answer
            for (const date in answers) {
              if (date < past90) continue
              const color = answers[date]
              if (color === 'green' || color === 'yellow' || color === 'red') {
                flightRiskByEmail[data.email].total++
                if (color === 'red') {
                  flightRiskByEmail[data.email].red++
                }
              }
            }
          }

          if (!engagementByEmail[data.email] || data.monthyear > engagementByEmail[data.email]._monthyear) {
            engagementByEmail[data.email] = {
              _monthyear: data.monthyear,
              daily_color: data.daily_color || '',
              feedback_color: data.feedback_color || '',
              super_feedback_color: data.super_feedback_color || '',
              recognition_color: data.recognition_color || '',
              super_recognition_color: data.super_recognition_color || '',
              peerfeedback_color: data.peerfeedback_color || '',
              happily_village_color: data.happily_village_color || '',
              favorite_spark_color: data.favorite_spark_color || '',
            }
          }
        })
      }

      // --- Process focus_summary_latest ---
      const focusByEmail: Record<string, { total: number, done: number, aligned: number }> = {}
      if (focusSnap) {
        focusSnap.forEach((doc: any) => {
          const data = doc.data()
          const email = doc.id
          if (!data.checklist) return
          let checklistArray: any[] = []
          const checklist = data.checklist
          if (Array.isArray(checklist)) {
            checklistArray = checklist
          } else if (typeof checklist === 'object' && checklist !== null) {
            checklistArray = Object.values(checklist)
          }
          const items = checklistArray.filter((item: any) => item && item.item)
          if (items.length === 0) return
          const total = items.length
          const done = items.filter((item: any) => item.status === 'done').length
          const aligned = items.filter((item: any) => item.goal_id && item.goal_id !== 'unmatched').length
          focusByEmail[email] = { total, done, aligned }
        })
      }

      // --- Process send_review_gem for gem rate ---
      const perfByEmail: Record<string, { gems: number, total: number }> = {}
      if (reviewSnap) {
        reviewSnap.forEach((doc: any) => {
          const data = doc.data()
          if (!data.receiver || isNaN(data.culture) || isNaN(data.goal)) return
          const email = data.receiver
          if (!perfByEmail[email]) perfByEmail[email] = { gems: 0, total: 0 }
          perfByEmail[email].total++
          // Gem = culture 3-4 AND goal 3-4 (matches performance.ts:887)
          if ((data.culture == 3 || data.culture == 4) && (data.goal == 3 || data.goal == 4)) {
            perfByEmail[email].gems++
          }
        })
      }

      // --- Process here_point for recognition counts, alignment quality, and unique pairs ---
      const recognitionByEmail: Record<string, { given: number, received: number }> = {}
      const alignmentByEmail: Record<string, { effortReceived: number, totalReceived: number }> = {}
      // Track unique recognition partners per person (for reciprocity count)
      const recognitionSentTo: Record<string, Set<string>> = {}
      const recognitionReceivedFrom: Record<string, Set<string>> = {}
      if (hereSnap) {
        hereSnap.forEach((doc: any) => {
          const data = doc.data()
          const sender = data.sender_email
          const receiver = data.send_to_email
          if (sender) {
            if (!recognitionByEmail[sender]) recognitionByEmail[sender] = { given: 0, received: 0 }
            recognitionByEmail[sender].given++
            if (receiver) {
              if (!recognitionSentTo[sender]) recognitionSentTo[sender] = new Set()
              recognitionSentTo[sender].add(receiver)
            }
          }
          if (receiver) {
            if (!recognitionByEmail[receiver]) recognitionByEmail[receiver] = { given: 0, received: 0 }
            recognitionByEmail[receiver].received++
            if (sender) {
              if (!recognitionReceivedFrom[receiver]) recognitionReceivedFrom[receiver] = new Set()
              recognitionReceivedFrom[receiver].add(sender)
            }
          }
          if (data.core_value_ai && receiver) {
            let maxRating = 0
            for (const key in data.core_value_ai) {
              const r = parseFloat(data.core_value_ai[key].rating)
              if (r > maxRating) maxRating = r
            }
            if (!alignmentByEmail[receiver]) alignmentByEmail[receiver] = { effortReceived: 0, totalReceived: 0 }
            alignmentByEmail[receiver].effortReceived += maxRating
            alignmentByEmail[receiver].totalReceived++
          }
        })
      }

      // --- Process peerfeedback for unique peer counts + Truly/Exceeding received ---
      const feedbackGivenByEmail: Record<string, number> = {}
      const trulyReceivedByEmail: Record<string, number> = {}
      // Track unique peers for peer trust count
      const feedbackSentTo: Record<string, Set<string>> = {}
      const feedbackReceivedFrom: Record<string, Set<string>> = {}
      if (feedbackSnap) {
        feedbackSnap.forEach((doc: any) => {
          const data = doc.data()
          const sender = data.email
          const receiver = data.email_to
          // Count total feedback given (for trust score)
          if (sender) {
            feedbackGivenByEmail[sender] = (feedbackGivenByEmail[sender] || 0) + 1
            // Track unique peers sent to
            if (receiver) {
              if (!feedbackSentTo[sender]) feedbackSentTo[sender] = new Set()
              feedbackSentTo[sender].add(receiver)
            }
          }
          // Track unique peers received from
          if (receiver) {
            if (!feedbackReceivedFrom[receiver]) feedbackReceivedFrom[receiver] = new Set()
            if (sender) feedbackReceivedFrom[receiver].add(sender)
          }
          // Count Truly/Exceeding feedback received
          if (receiver && data.suggest && data.suggest.line1_en) {
            const rating = data.suggest.line1_en
            if (rating === 'Truly' || rating === 'Exceeding') {
              trulyReceivedByEmail[receiver] = (trulyReceivedByEmail[receiver] || 0) + 1
            }
          }
        })
      }

      // --- Compute scores for all employees ---
      function colorScore(c: string) {
        if (c === 'green') return 1.0
        if (c === 'yellow') return 0.5
        return 0
      }

      const employees = rootState.people.starperformall || []
      const allGivenCounts: number[] = []
      const allReceivedCounts: number[] = []
      for (let i = 0; i < employees.length; i++) {
        const emp = employees[i]
        if (!emp.email) continue
        const emailScores: any = {}

        // Flight Risk Score (0-100, higher = more risk)
        // Non-linear scale: even a small % of red days is a strong signal
        // sqrt(redRate / 0.25) * 100 → 5% red = ~45, 10% = ~63, 25%+ = 100
        const fr = flightRiskByEmail[emp.email]
        if (fr && fr.total > 0) {
          const redRate = fr.red / fr.total
          const scaled = Math.min(1, Math.sqrt(redRate / 0.25))
          emailScores.computed_flight_risk = Math.round(scaled * 1000) / 10
        } else {
          emailScores.computed_flight_risk = 0
        }

        // Engagement Score (0-100, higher = better)
        // Computed from report_monthly engagement color indicators
        // Weighted: daily check-in (30%), feedback (20%), recognition (20%),
        //   super_feedback (10%), super_recognition (10%), peer_feedback (10%)
        const engData = engagementByEmail[emp.email]
        if (engData) {
          const weighted =
            colorScore(engData.daily_color) * 0.30 +
            colorScore(engData.feedback_color) * 0.20 +
            colorScore(engData.recognition_color) * 0.20 +
            colorScore(engData.super_feedback_color) * 0.10 +
            colorScore(engData.super_recognition_color) * 0.10 +
            colorScore(engData.peerfeedback_color) * 0.10
          emailScores.computed_engagement = Math.round(weighted * 1000) / 10
        } else {
          emailScores.computed_engagement = 0
        }

        // Focus Score (0-100): number of focus items, 5 items = 100
        const focusData = focusByEmail[emp.email]
        const focusCount = focusData ? focusData.total : 0
        emailScores.computed_focus = Math.round(Math.min(focusCount / 5, 1) * 1000) / 10

        // Goal Alignment (0-100, higher = better)
        if (focusData && focusData.total > 0) {
          emailScores.computed_goal_alignment = Math.round((focusData.aligned / focusData.total) * 1000) / 10
        } else {
          emailScores.computed_goal_alignment = 0
        }

        // Performance Score (0-100) — gem rate × confidence
        // sqrt confidence curve: 1 boost ≈ 41% credit, 3 ≈ 71%, 6+ = full credit
        const perfData = perfByEmail[emp.email]
        if (perfData && perfData.total > 0) {
          const gemRate = perfData.gems / perfData.total
          const confidence = Math.min(Math.sqrt(perfData.total / 6), 1.0)
          emailScores.computed_performance = Math.round(gemRate * confidence * 1000) / 10
        } else {
          emailScores.computed_performance = 0
        }

        // Peer Trust Count: unique feedback peers + reciprocal recognition pairs
        const fbSent = feedbackSentTo[emp.email] ? feedbackSentTo[emp.email].size : 0
        const fbReceived = feedbackReceivedFrom[emp.email] ? feedbackReceivedFrom[emp.email].size : 0
        let reciprocalPairs = 0
        const sentTo = recognitionSentTo[emp.email]
        const recvFrom = recognitionReceivedFrom[emp.email]
        if (sentTo && recvFrom) {
          sentTo.forEach(function (peer) { if (recvFrom.has(peer)) reciprocalPairs++ })
        }
        emailScores.computed_peer_trust = fbSent + fbReceived + reciprocalPairs

        // Trust Score + recognition — computed in second pass after median normalization

        // Collect recognition counts for median calculation
        const recData = recognitionByEmail[emp.email]
        allGivenCounts.push(recData ? (recData.given || 0) : 0)
        allReceivedCounts.push(recData ? (recData.received || 0) : 0)

        scores[emp.email] = emailScores
      }

      // --- Median-based normalization for recognition + trust ---

      function getMedian(arr: number[]): number {
        if (arr.length === 0) return 1
        const nonZero = arr.filter(v => v > 0).sort((a, b) => a - b)
        if (nonZero.length === 0) return 1
        const mid = Math.floor(nonZero.length / 2)
        return nonZero.length % 2 === 0
          ? (nonZero[mid - 1] + nonZero[mid]) / 2
          : nonZero[mid]
      }

      const medianGiven = getMedian(allGivenCounts)
      const medianReceived = getMedian(allReceivedCounts)

      for (let i = 0; i < employees.length; i++) {
        const emp = employees[i]
        if (!emp.email || !scores[emp.email]) continue
        const recData = recognitionByEmail[emp.email]
        const given = recData ? (recData.given || 0) : 0
        const received = recData ? (recData.received || 0) : 0

        scores[emp.email].computed_giving_recognition =
          Math.round(Math.min(given / (2 * medianGiven), 1) * 1000) / 10
        scores[emp.email].computed_receiving_recognition =
          Math.round(Math.min(received / (2 * medianReceived), 1) * 1000) / 10

        // Trust Score (0-100, higher = better)
        // 1. Peer Feedback Activity (30%) — giving + receiving strong ratings
        //    Use weighted blend: stronger signal drives 70%, weaker adds 30% bonus
        const feedbackGiven = feedbackGivenByEmail[emp.email] || 0
        const trulyCount = trulyReceivedByEmail[emp.email] || 0
        const feedbackGivenNorm = Math.min(feedbackGiven / 5, 1.0)
        const trulyNorm = Math.min(trulyCount / 5, 1.0)
        const peerHigh = Math.max(feedbackGivenNorm, trulyNorm)
        const peerLow = Math.min(feedbackGivenNorm, trulyNorm)
        const peerFeedbackActivity = peerHigh * 0.7 + peerLow * 0.3

        // 2. Recognition Reciprocity (30%) — balanced give/receive
        //    Same blend: stronger signal drives 70%, weaker adds 30% bonus
        const givenNorm = Math.min(given / (2 * medianGiven), 1.0)
        const receivedNorm = Math.min(received / (2 * medianReceived), 1.0)
        const recHigh = Math.max(givenNorm, receivedNorm)
        const recLow = Math.min(givenNorm, receivedNorm)
        const recognitionReciprocity = recHigh * 0.7 + recLow * 0.3

        // 3. Network Strength (20%) — from emp.network_type
        let networkStrength = 0.1
        const netType = (emp.network_type || '').toLowerCase()
        if (netType.indexOf('super') >= 0) networkStrength = 1.0
        else if (netType.indexOf('high') >= 0) networkStrength = 0.7
        else if (netType.indexOf('medium') >= 0) networkStrength = 0.4
        else if (netType.indexOf('low') >= 0) networkStrength = 0.1

        // 4. Recognition Quality Received (20%) — average core_value_ai rating from here_point
        const alignInfo = alignmentByEmail[emp.email]
        const alignmentReceived = (alignInfo && alignInfo.totalReceived > 0)
          ? alignInfo.effortReceived / alignInfo.totalReceived : 0
        const recognitionQuality = Math.min(alignmentReceived / 5, 1.0)

        scores[emp.email].computed_trust = Math.round(
          (peerFeedbackActivity * 0.30 + recognitionReciprocity * 0.30 + networkStrength * 0.20 + recognitionQuality * 0.20) * 1000
        ) / 10
      }

      commit('setCompositeScores', scores)
    },
  },
}
